import { useQuery } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { AiServiceSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/forms';
import { Modal } from '../../ui/Modal';
import { Card, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/**
 * IDN-06: service accounts, the AI identities without an owner. They sign in with admin-issued client
 * credentials, read all projects and write only where granted.
 */
export function ServiceAccountsPage() {
  usePageTitle('Service accounts');
  useCrumbs([{ label: 'Administration' }, { label: 'Service accounts' }]);
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['ai-services'],
    queryFn: () => api.get<AiServiceSummary[]>('/admin/ai-services'),
    enabled: admin,
  });
  const [creating, setCreating] = useState(false);
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="Service accounts"
        subtitle="Identities for automation that runs without a person, such as CI builders and deploy workflows. They sign in with a client ID and secret, read all projects and write only where granted. None of them can approve work, releases or rollbacks."
        actions={<Button onClick={() => setCreating(true)}>Add service account</Button>}
      />
      <QueryView query={q}>
        {(list) => {
          const accounts = list.filter((s) => !s.owner);
          return (
            <Card>
              {accounts.length === 0 ? (
                <EmptyState icon={KeyRound} title="No service accounts yet">
                  Add one for each builder or workflow, then issue it a credential.
                </EmptyState>
              ) : (
                <ServiceAccountTable rows={accounts} />
              )}
            </Card>
          );
        }}
      </QueryView>
      {creating && <AddServiceAccount onClose={() => setCreating(false)} />}
    </div>
  );
}

function ServiceAccountTable({ rows }: { rows: AiServiceSummary[] }) {
  const navigate = useNavigate();
  const open = (id: string) => navigate(`/admin/service-accounts/${id}`);
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Service account</th>
            <th>State</th>
            <th className="num">Active credentials</th>
            <th>Last used</th>
            <th>Write grants</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className="clickable" onClick={() => open(s.id)}>
              <td>
                <a
                  href={`/admin/service-accounts/${s.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    open(s.id);
                  }}
                  className="primary-cell"
                >
                  {s.name}
                </a>
                {s.description && <div className="small muted">{s.description}</div>}
              </td>
              <td>{s.active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}</td>
              <td className="num">{s.activeCredentials}</td>
              <td className="small">
                <Timestamp value={s.lastUsedAt} relative />
              </td>
              <td className="small">
                {s.grants.length ? (
                  s.grants.map((g) => `${g.projectKey} (${g.capabilities})`).join(', ')
                ) : (
                  <span className="muted">None</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddServiceAccount({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const create = useAction(() => api.post<{ id: string }>('/admin/ai-services', { name, description }), {
    success: 'Service account added',
    invalidate: [['ai-services']],
    onSuccess: (r) => navigate(`/admin/service-accounts/${r.id}`),
  });
  return (
    <Modal
      title="Add service account"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate(undefined)} loading={create.isPending} disabled={!name}>
            Add service account
          </Button>
        </>
      }
    >
      <p className="small muted">
        For automation that runs unattended. People don't need this: their personal agent is created when they
        sign in.
      </p>
      <Field label="Name" hint="Shown as the actor on comments, claims and evidence.">
        {(p) => (
          <Input
            {...p}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. ci-builder"
            data-autofocus
          />
        )}
      </Field>
      <Field label="Description" optional>
        {(p) => (
          <Textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      <p className="small muted">
        Next, issue it a credential and grant project capabilities. New service accounts have no write grants.
      </p>
      <ErrorAlert error={create.error} />
    </Modal>
  );
}
